import axios from 'axios';
import type {
  StockQuote,
  StockHistory,
  StockProfile,
  SignalOverview,
  DetailedSignal,
  TimeInterval,
} from '../../shared';

// 使用环境变量或相对路径，走 Vite 代理
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000,
});

const apiClient = api;

// Token management
const TOKEN_KEY = 'stock_s1_token';

export const setToken = (token: string) => localStorage.setItem(TOKEN_KEY, token);
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const removeToken = () => localStorage.removeItem(TOKEN_KEY);

export const getUser = () => {
  const token = getToken();
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload) as { username: string; sub: number; role?: 'ADMIN' | 'USER' };
  } catch (e) {
    return null;
  }
};

// Request interceptor
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      removeToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (username: string, password: string) => {
    const { data } = await apiClient.post('/auth/login', { username, password });
    return data;
  },
  getCurrentUser: async () => {
    const { data } = await apiClient.get('/auth/me');
    return data;
  },
  getUsers: async () => {
    const { data } = await apiClient.get('/auth/users');
    return data;
  },
  register: async (username: string, password: string, role: 'ADMIN' | 'USER') => {
    const { data } = await apiClient.post('/auth/register', { username, password, role });
    return data;
  },
  updatePassword: async (userId: number, password: string) => {
    const { data } = await apiClient.post(`/auth/users/${userId}/password`, { password });
    return data;
  },
  updateUser: async (userId: number, updates: { username?: string; role?: 'ADMIN' | 'USER' }) => {
    const { data } = await apiClient.patch(`/auth/users/${userId}`, updates);
    return data;
  },
  githubLogin: async (code: string) => {
    const { data } = await apiClient.post('/auth/github/login', { code });
    return data;
  },
  bindGithub: async (code: string) => {
    const { data } = await apiClient.post('/auth/github/bind', { code });
    return data;
  },
  unbindGithub: async () => {
    const { data } = await apiClient.delete('/auth/github/unbind');
    return data;
  },
  deleteUser: async (userId: number) => {
    const { data } = await apiClient.delete(`/auth/users/${userId}`);
    return data;
  },
};

export const stockApi = {
  getQuote: async (ticker: string): Promise<StockQuote> => {
    const { data } = await apiClient.get(`/stock/${ticker}`);
    return data;
  },

  getHistory: async (
    ticker: string,
    interval: TimeInterval = '1d',
    range: string = '1mo'
  ): Promise<StockHistory> => {
    const { data } = await apiClient.get(`/stock/${ticker}/history`, {
      params: { interval, range },
    });
    return data;
  },

  getProfile: async (ticker: string): Promise<StockProfile> => {
    const { data } = await apiClient.get(`/stock/${ticker}/profile`);
    return data;
  },
};

export const signalApi = {
  getOverview: async (ticker: string): Promise<SignalOverview> => {
    const { data} = await apiClient.get(`/signal/${ticker}`);
    return data;
  },

  getDetailed: async (ticker: string): Promise<DetailedSignal> => {
    const { data } = await apiClient.get(`/signal/${ticker}/detailed`);
    return data;
  },
};

export const historyApi = {
  getHistory: async (type = 'STOCK_SEARCH') => {
    const { data } = await apiClient.get('/history', { params: { type } });
    return data;
  },

  addHistory: async (type: string, data: any) => {
    const { data: result } = await apiClient.post('/history', { type, data });
    return result;
  },

  deleteHistory: async (historyId: number) => {
    await apiClient.delete(`/history/${historyId}`);
  },

  clearHistory: async (type = 'STOCK_SEARCH') => {
    await apiClient.delete('/history', { params: { type } });
  },

  cleanupDuplicates: async (type = 'STOCK_SEARCH'): Promise<{ removed: number }> => {
    const { data } = await apiClient.post('/history/cleanup', null, { params: { type } });
    return data;
  },
};
